import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  Zap, Sparkles, Target, Mail, FileUp, FileDown, BarChart3, ArrowRight, Check,
} from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

export default function Landing() {
  const { t, user } = useApp();
  const navigate = useNavigate();

  const features = [
    { icon: Sparkles, title: t("feat1Title"), desc: t("feat1Desc") },
    { icon: Target, title: t("feat2Title"), desc: t("feat2Desc") },
    { icon: Mail, title: t("feat3Title"), desc: t("feat3Desc") },
    { icon: FileUp, title: t("feat4Title"), desc: t("feat4Desc") },
    { icon: FileDown, title: t("feat5Title"), desc: t("feat5Desc") },
    { icon: BarChart3, title: t("feat6Title"), desc: t("feat6Desc") },
  ];

  const goStart = () => navigate(user ? "/app/dashboard" : "/register");

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b bg-card/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Zap className="h-4 w-4" />
            </span>
            <span className="font-heading text-lg font-semibold tracking-tight">
              DomainPitch<span className="text-primary">AI</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="landing-login-button">
              {t("login")}
            </Button>
            <Button size="sm" onClick={goStart} data-testid="landing-cta-button">
              {t("getStarted")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-gradient">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Powered by Claude Sonnet 4.5
            </span>
            <h1 className="mt-5 font-heading text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              {t("heroTitle")}
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
              {t("heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={goStart} data-testid="hero-primary-cta" className="gap-2">
                {t("heroCta")} <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="secondary" onClick={() => navigate(user ? "/app/pricing" : "/login")}>
                {t("heroSecondary")}
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-6"
          >
            <Card className="card-lift overflow-hidden p-0">
              <div className="flex items-center justify-between border-b bg-secondary/50 px-4 py-3">
                <span className="font-mono text-sm font-medium">cloudsecure.io</span>
                <StatusBadge status="pitched" />
              </div>
              <div className="space-y-4 p-5">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Valuation</div>
                  <div className="mt-1 font-mono text-lg font-semibold text-primary">EUR 10,000 – 18,000</div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Pitch</div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/80">
                    A premium two-keyword domain at the intersection of cloud and security — the perfect brand home for a cloud-native security startup...
                  </p>
                </div>
                <div className="rounded-lg border bg-secondary/40 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Outbound email</div>
                  <p className="mt-1 text-sm text-foreground/80">Subject: cloudsecure.io — your next security brand...</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="font-heading text-3xl font-semibold tracking-tight">{t("featuresTitle")}</h2>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <Card key={i} className="card-lift p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <Card className="flex flex-col items-center gap-4 p-10 text-center">
          <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">{t("finalCtaTitle")}</h2>
          <p className="max-w-md text-muted-foreground">{t("finalCtaDesc")}</p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={goStart} className="gap-2">
              {t("heroCta")} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-primary" /> {t("pitchesLifetime")}</span>
            <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-primary" /> {t("stripeTrust")}</span>
          </div>
        </Card>
      </section>

      <footer className="border-t py-8 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} DomainPitch AI
      </footer>
    </div>
  );
}
